/**
 * StrategyConfiguration Component
 * 
 * Strategy-level configuration including:
 * - Range breakout settings
 * - Entry and exit time configuration
 * - Trading type (intraday/positional)
 * - Strategy-level target profit and stop loss
 * - Move SL to cost option
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import React from 'react';
import { Plus, X } from 'lucide-react';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Checkbox from '../../ui/Checkbox';
import Tooltip from '../../ui/Tooltip';
import Dropdown from '../../ui/Dropdown';
import SegmentedControl from '../../ui/SegmentedControl';
import Slider from '../../ui/Slider';
import HelpTip from '../../ui/HelpTip';
import { StrategyConfig } from '../../../types/strategy';
import {
  EXPIRY_TYPE,
  MARKET_HOUR_OPTIONS,
  getMarketMinuteOptions,
  clampToMarketTime,
  MTM_TYPE_OPTIONS
} from '../../../utils/strategy';
import {
  TRADING_TYPE_OPTIONS,
  INTRADAY_EXIT_MODE_OPTIONS,
  MTM_TYPE,
} from '../../../constants/tradingConstants';
import { STRATEGY_HELP } from '../../../utils/strategy/strategyHelpText';
import { getStrategyMtmInvalidReason } from '../../../utils/strategy/riskValidators';

interface StrategyConfigurationProps {
  config: StrategyConfig;
  onUpdate: (updates: Partial<StrategyConfig>) => void;
  /** True when at least one leg has stop_loss.enabled — gates Move SL to Cost. */
  slConfiguredOnAnyLeg?: boolean;
  /** True when any leg is SELL — a Combined-Premium % stop loss may exceed 100% only
   *  when the book has short exposure (a long-only book's max loss is the premium). */
  hasSellLeg?: boolean;
  /** True when any leg is BUY — a Combined-Premium % target may exceed 100% only when
   *  the book has a long leg (an all-SELL book's max profit is the premium collected). */
  hasBuyLeg?: boolean;
}

const StrategyConfiguration: React.FC<StrategyConfigurationProps> = ({ config, onUpdate, slConfiguredOnAnyLeg = false, hasSellLeg = false, hasBuyLeg = false }) => {
  const [targetProfitInputValue, setTargetProfitInputValue] = React.useState(String(config.targetProfit.value));
  const [mtmStopLossInputValue, setMtmStopLossInputValue] = React.useState(String(config.mtmStopLoss.value));

  // Strategy MTM controls start collapsed behind a "+" affordance unless already
  // configured (any non-zero value — invalid values stay visible so they can be
  // fixed). Clicking the "×" collapses and resets to 0 = disabled.
  const [targetProfitOpen, setTargetProfitOpen] = React.useState(!!Number(config.targetProfit.value));
  const [mtmStopLossOpen, setMtmStopLossOpen] = React.useState(!!Number(config.mtmStopLoss.value));

  const handleRangeBreakoutChange = (rangeBreakout: boolean) => {
    // Turning Range Breakout off also clears its overnight carry-forward —
    // the flag has no meaning when ORB itself is off, and re-enabling ORB
    // later should start from the safe default (false).
    onUpdate(rangeBreakout
      ? { rangeBreakout }
      : { rangeBreakout, orbContinueOvernight: false });
  };

  const handleOrbContinueOvernightChange = (orbContinueOvernight: boolean) => {
    onUpdate({ orbContinueOvernight });
  };

  // ORB carry-forward applies only to overnight-capable modes:
  //   - NRML product (MIS auto-squares at broker EOD)
  //   - POSITIONAL, or INTRADAY + NEXT_DAY_BTST (SAME_DAY enters+exits same day,
  //     so preserving an unfilled entry to tomorrow has no valid exit_time)
  // Auto-clear if the user switches into a non-overnight mode while it's on.
  // Mirrors the moveSlToCost self-clearing pattern above.
  const orbCarryForwardAllowed = (
    config.productType === 'NRML'
    && (config.tradingType === 'POSITIONAL'
        || (config.tradingType === 'INTRADAY'
            && config.intradayExitMode === 'NEXT_DAY_BTST'))
  );
  React.useEffect(() => {
    if (config.orbContinueOvernight && !orbCarryForwardAllowed) {
      onUpdate({ orbContinueOvernight: false });
    }
  }, [config.orbContinueOvernight, orbCarryForwardAllowed, onUpdate]);

  React.useEffect(() => {
    setTargetProfitInputValue(String(config.targetProfit.value));
  }, [config.targetProfit.value]);

  React.useEffect(() => {
    setMtmStopLossInputValue(String(config.mtmStopLoss.value));
  }, [config.mtmStopLoss.value]);

  // Move SL to Cost needs a leg-level SL to act on — auto-clear it when the last
  // SL is removed (mirrors the backend validator's gating).
  React.useEffect(() => {
    if (config.moveSlToCost && !slConfiguredOnAnyLeg) {
      onUpdate({ moveSlToCost: false });
    }
  }, [config.moveSlToCost, slConfiguredOnAnyLeg, onUpdate]);

  const handleEntryTimeChange = (field: 'entryTimeHour' | 'entryTimeMinute', value: string) => {
    onUpdate({ [field]: value });
  };

  const handleExitTimeChange = (field: 'exitTimeHour' | 'exitTimeMinute', value: string) => {
    onUpdate({ [field]: value });
  };

  const handleRangeBreakoutTimeChange = (field: 'rangeBreakoutTimeHour' | 'rangeBreakoutTimeMinute', value: string) => {
    onUpdate({ [field]: value });
  };

  const handleTradingTypeChange = (tradingType: 'INTRADAY' | 'POSITIONAL') => {
    onUpdate({ tradingType });
  };

  const handleIntradayExitModeChange = (intradayExitMode: 'SAME_DAY' | 'NEXT_DAY_BTST') => {
    if (intradayExitMode === 'SAME_DAY') {
      const entry = clampToMarketTime(config.entryTimeHour, config.entryTimeMinute);
      const exit = clampToMarketTime(config.exitTimeHour, config.exitTimeMinute);
      onUpdate({
        intradayExitMode,
        // Rollover only meaningful for NEXT_DAY_BTST — clear it on SAME_DAY so
        // the form can't submit a stale True against an unsupported combo.
        expiryDayRollover: false,
        entryTimeHour: entry.hour,
        entryTimeMinute: entry.minute,
        exitTimeHour: exit.hour,
        exitTimeMinute: exit.minute,
      });
    } else {
      onUpdate({ intradayExitMode });
    }
  };

  const handleExpiryDayRolloverChange = (expiryDayRollover: boolean) => {
    onUpdate({ expiryDayRollover });
  };

  // Always constrain time pickers to market hours (09:16 - 15:39)
  const entryHourOptions = MARKET_HOUR_OPTIONS;
  const exitHourOptions = MARKET_HOUR_OPTIONS;
  const entryMinuteOptions = getMarketMinuteOptions(config.entryTimeHour);
  const exitMinuteOptions = getMarketMinuteOptions(config.exitTimeHour);


  const handleTargetProfitChange = (field: 'type' | 'value', value: any) => {
    onUpdate({
      targetProfit: { ...config.targetProfit, [field]: value }
    });
  };

  const handleMtmStopLossChange = (field: 'type' | 'value', value: any) => {
    onUpdate({
      mtmStopLoss: { ...config.mtmStopLoss, [field]: value }
    });
  };

  const handleTargetProfitInputChange = (rawValue: string) => {
    setTargetProfitInputValue(rawValue);
    if (rawValue === '') {
      handleTargetProfitChange('value', 0);
      return;
    }
    const parsedValue = Number(rawValue);
    if (!Number.isNaN(parsedValue)) {
      handleTargetProfitChange('value', parsedValue);
    }
  };

  const handleMtmStopLossInputChange = (rawValue: string) => {
    setMtmStopLossInputValue(rawValue);
    if (rawValue === '') {
      handleMtmStopLossChange('value', 0);
      return;
    }
    const parsedValue = Number(rawValue);
    if (!Number.isNaN(parsedValue)) {
      handleMtmStopLossChange('value', parsedValue);
    }
  };

  const handleTargetProfitInputBlur = () => {
    const trimmedValue = targetProfitInputValue.trim();
    const parsedValue = Number(trimmedValue);
    if (trimmedValue === '' || Number.isNaN(parsedValue)) {
      handleTargetProfitChange('value', 0);
      return;
    }
    handleTargetProfitChange('value', parsedValue);
  };

  const handleMtmStopLossInputBlur = () => {
    const trimmedValue = mtmStopLossInputValue.trim();
    const parsedValue = Number(trimmedValue);
    if (trimmedValue === '' || Number.isNaN(parsedValue)) {
      handleMtmStopLossChange('value', 0);
      return;
    }
    handleMtmStopLossChange('value', parsedValue);
  };

  const closeTargetProfit = () => {
    setTargetProfitOpen(false);
    handleTargetProfitChange('value', 0);
  };

  const closeMtmStopLoss = () => {
    setMtmStopLossOpen(false);
    handleMtmStopLossChange('value', 0);
  };

  // Inline validation for the strategy-level MTM controls (0 = disabled = valid).
  // Combined-Premium % caps are leg-aware: the stop loss may exceed 100% only with
  // short exposure (hasSellLeg); the target only with a long leg (hasBuyLeg).
  const targetProfitError = getStrategyMtmInvalidReason(config.targetProfit, 'Target Profit', hasBuyLeg);
  const mtmStopLossError = getStrategyMtmInvalidReason(config.mtmStopLoss, 'Stop Loss', hasSellLeg);

  const handleTradingDaysChange = (field: 'entryTradingDaysBeforeExpiry' | 'exitTradingDaysBeforeExpiry', value: number) => {
    onUpdate({ [field]: value });
  };

  // Get slider range based on expiry type
  const getSliderRange = () => {
    return config.expiryType === EXPIRY_TYPE.WEEKLY ? { max: 4, min: 0 } : { max: 24, min: 0 };
  };

  const getTradingDaysText = (value: number) => {
    return `${value} trading days before ${config.expiryType} expiry (excluding holidays)`;
  };

  // Get default values based on expiry type
  const getDefaultTradingDays = () => {
    if (config.expiryType === EXPIRY_TYPE.WEEKLY) {
      return { entry: 4, exit: 0 };
    } else {
      return { entry: 24, exit: 0 };
    }
  };

  // Reset trading days when expiry type changes. For next_weekly / next_monthly
  // BTST strategies, auto-enable expiryDayRollover — the rollover is semantically
  // implied by "next" expiry type and users should not need to configure it.
  //
  // Intentionally narrow-deps: re-firing on `intradayExitMode` would clobber the
  // user's just-edited trading-days values; `getDefaultTradingDays` and `onUpdate`
  // are recreated every render and would loop. The effect only needs to fire on
  // expiryType transitions.
  React.useEffect(() => {
    const defaults = getDefaultTradingDays();
    const updates: Record<string, unknown> = {
      entryTradingDaysBeforeExpiry: defaults.entry,
      exitTradingDaysBeforeExpiry: defaults.exit,
    };
    const isNextExpiry =
      config.expiryType === EXPIRY_TYPE.NEXT_WEEKLY ||
      config.expiryType === EXPIRY_TYPE.NEXT_MONTHLY;
    if (isNextExpiry && config.intradayExitMode === 'NEXT_DAY_BTST') {
      updates.expiryDayRollover = true;
    }
    onUpdate(updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.expiryType]);

  return (
    <div>
      <div className="space-y-4">
        {/* Stack the Move-SL-to-Cost row and the Trading-Type row vertically on
            mobile — the SegmentedControl (INTRADAY/POSITIONAL) is too wide to
            sit on the same row as the checkbox at ~360 px. Side-by-side from sm+. */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Move SL to Cost — disabled until at least one leg has a stop loss */}
          <div className="flex items-center gap-1">
            {slConfiguredOnAnyLeg ? (
              <Checkbox
                checked={config.moveSlToCost}
                onChange={(moveSlToCost) => onUpdate({ moveSlToCost })}
                label={<span className="text-xs font-medium text-gray-700 dark:text-gray-200 whitespace-nowrap">Move SL to Cost</span>}
              />
            ) : (
              <Tooltip text="Enable stop loss on at least one leg to use Move SL to Cost">
                <span className="inline-block">
                  <Checkbox
                    checked={false}
                    onChange={() => {}}
                    disabled
                    label={<span className="text-xs font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap">Move SL to Cost</span>}
                  />
                </span>
              </Tooltip>
            )}
            <HelpTip entry={STRATEGY_HELP.moveSlToCost} placement="side" />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                Trading Type
              </span>
              <HelpTip entry={STRATEGY_HELP.tradingType} />
            </div>
            <SegmentedControl
              value={config.tradingType}
              onChange={(v) => handleTradingTypeChange(v as 'INTRADAY' | 'POSITIONAL')}
              options={TRADING_TYPE_OPTIONS}
              size="sm"
            />
          </div>
        </div>

        <div className="space-y-4">
        {/* Time Configuration Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Entry Time Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="flex items-center gap-1">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                      {config.rangeBreakout ? 'Range Start Time' : 'Entry Time'}
                    </label>
                    <HelpTip entry={STRATEGY_HELP.entryTime} />
                  </div>
                </div>
                <Checkbox
                  checked={config.rangeBreakout}
                  onChange={handleRangeBreakoutChange}
                  label={
                    <span className="flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                      Range Breakout
                      <HelpTip entry={STRATEGY_HELP.rangeBreakout} />
                    </span>
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hour</label>
                  <Dropdown
                    value={config.entryTimeHour}
                    onChange={(v) => handleEntryTimeChange('entryTimeHour', v)}
                    options={entryHourOptions}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Minute</label>
                  <Dropdown
                    value={config.entryTimeMinute}
                    onChange={(v) => handleEntryTimeChange('entryTimeMinute', v)}
                    options={entryMinuteOptions}
                  />
                </div>
              </div>
              
              {/* Range Breakout Time */}
              {config.rangeBreakout && (
                <div className="pt-4 border-t border-gray-200/50 dark:border-gray-600/50">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <div className="flex items-center gap-1">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                        Range End Time
                      </label>
                      <HelpTip entry={STRATEGY_HELP.rangeEndTime} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Dropdown
                      value={config.rangeBreakoutTimeHour}
                      onChange={(v) => handleRangeBreakoutTimeChange('rangeBreakoutTimeHour', v)}
                      options={MARKET_HOUR_OPTIONS}
                      size="sm"
                    />
                    <Dropdown
                      value={config.rangeBreakoutTimeMinute}
                      onChange={(v) => handleRangeBreakoutTimeChange('rangeBreakoutTimeMinute', v)}
                      options={getMarketMinuteOptions(config.rangeBreakoutTimeHour)}
                      size="sm"
                    />
                  </div>

                  {/* ORB carry-forward — visible only for overnight-capable modes
                      (NRML product AND (POSITIONAL or INTRADAY+NEXT_DAY_BTST)).
                      Hidden for SAME_DAY intraday since entry+exit run within
                      one trading day. */}
                  {orbCarryForwardAllowed && (
                    <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-600/50">
                      <Checkbox
                        checked={config.orbContinueOvernight}
                        onChange={handleOrbContinueOvernightChange}
                        label={
                          <span className="flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                            Carry forward to next day
                            <HelpTip entry={STRATEGY_HELP.orbContinueOvernight} />
                          </span>
                        }
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Trading Days Slider - Only for Positional */}
              {config.tradingType === 'POSITIONAL' && (
                <div className="pt-4 border-t border-gray-200/50 dark:border-gray-600/50">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Slider
                        min={getSliderRange().min}
                        max={getSliderRange().max}
                        value={getSliderRange().max - config.entryTradingDaysBeforeExpiry}
                        onChange={(n) => handleTradingDaysChange('entryTradingDaysBeforeExpiry', getSliderRange().max - n)}
                        aria-label="Entry trading days before expiry"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        {getTradingDaysText(config.entryTradingDaysBeforeExpiry)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Exit Time Card */}
          <div className="bg-white/90 dark:bg-gray-800/90 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <div className="flex items-center gap-1">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Exit Time
                  </label>
                  <HelpTip entry={STRATEGY_HELP.exitTime} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hour</label>
                  <Dropdown
                    value={config.exitTimeHour}
                    onChange={(v) => handleExitTimeChange('exitTimeHour', v)}
                    options={exitHourOptions}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Minute</label>
                  <Dropdown
                    value={config.exitTimeMinute}
                    onChange={(v) => handleExitTimeChange('exitTimeMinute', v)}
                    options={exitMinuteOptions}
                  />
                </div>
              </div>
              
              {/* Exit Mode Radio Buttons - Only show for Intraday */}
              {config.tradingType === 'INTRADAY' && (
                <div className="pt-3 border-t border-gray-200/50 dark:border-gray-600/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      Exit Mode
                    </span>
                    <HelpTip entry={STRATEGY_HELP.intradayExitMode} />
                  </div>
                  <SegmentedControl
                    value={config.intradayExitMode}
                    onChange={(v) => handleIntradayExitModeChange(v as 'SAME_DAY' | 'NEXT_DAY_BTST')}
                    options={INTRADAY_EXIT_MODE_OPTIONS}
                    size="sm"
                    fullWidth
                  />

                  {/* Continue on expiry days — BTST/STBT only.
                      Hidden for next_weekly / next_monthly because rollover is
                      always enabled implicitly for next_* (semantically implied
                      by choosing the "next" contract). */}
                  {config.intradayExitMode === 'NEXT_DAY_BTST' &&
                    config.expiryType !== EXPIRY_TYPE.NEXT_WEEKLY &&
                    config.expiryType !== EXPIRY_TYPE.NEXT_MONTHLY && (
                    <div className="mt-3">
                      <Checkbox
                        checked={config.expiryDayRollover}
                        onChange={handleExpiryDayRolloverChange}
                        label={
                          <span className="flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                            Continue on expiry days (next contract)
                            <HelpTip entry={STRATEGY_HELP.expiryDayRollover} />
                          </span>
                        }
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Trading Days Slider - Only for Positional */}
              {config.tradingType === 'POSITIONAL' && (
                <div className="pt-4 border-t border-gray-200/50 dark:border-gray-600/50">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Slider
                        min={getSliderRange().min}
                        max={getSliderRange().max}
                        value={getSliderRange().max - config.exitTradingDaysBeforeExpiry}
                        onChange={(n) => handleTradingDaysChange('exitTradingDaysBeforeExpiry', getSliderRange().max - n)}
                        aria-label="Exit trading days before expiry"
                      />
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        {getTradingDaysText(config.exitTradingDaysBeforeExpiry)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Strategy-level Risk Management */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {/* Target Profit */}
          <div>
            {targetProfitOpen ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Strategy Target Profit
                    </label>
                    <HelpTip entry={STRATEGY_HELP.strategyTargetProfit} />
                  </div>
                  <button
                    type="button"
                    onClick={closeTargetProfit}
                    aria-label="Remove strategy target profit"
                    className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={config.targetProfit.type}
                    onChange={(e) => handleTargetProfitChange('type', e.target.value)}
                    options={MTM_TYPE_OPTIONS}
                    className="h-10 text-sm"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={targetProfitInputValue}
                    onChange={(e) => handleTargetProfitInputChange(e.target.value)}
                    onBlur={handleTargetProfitInputBlur}
                    placeholder="Value (0 = off)"
                    className="h-10 text-sm"
                    error={targetProfitError || undefined}
                  />
                </div>
                {config.targetProfit.type === MTM_TYPE.TOTAL_MTM && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    ₹ for a 1× lot — scales with each account's lot multiplier.
                  </p>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setTargetProfitOpen(true)}
                className="flex items-center gap-1.5 w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Strategy Target Profit
              </button>
            )}
          </div>

          {/* MTM Stop Loss */}
          <div>
            {mtmStopLossOpen ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                      Strategy Stop Loss
                    </label>
                    <HelpTip entry={STRATEGY_HELP.strategyStopLoss} />
                  </div>
                  <button
                    type="button"
                    onClick={closeMtmStopLoss}
                    aria-label="Remove strategy stop loss"
                    className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={config.mtmStopLoss.type}
                    onChange={(e) => handleMtmStopLossChange('type', e.target.value)}
                    options={MTM_TYPE_OPTIONS}
                    className="h-10 text-sm"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={mtmStopLossInputValue}
                    onChange={(e) => handleMtmStopLossInputChange(e.target.value)}
                    onBlur={handleMtmStopLossInputBlur}
                    placeholder="Value (0 = off)"
                    className="h-10 text-sm"
                    error={mtmStopLossError || undefined}
                  />
                </div>
                {config.mtmStopLoss.type === MTM_TYPE.TOTAL_MTM && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    ₹ for a 1× lot — scales with each account's lot multiplier.
                  </p>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setMtmStopLossOpen(true)}
                className="flex items-center gap-1.5 w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Strategy Stop Loss
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
    </div>
  );
};

export default StrategyConfiguration;